import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getApiErrorMessage } from "@penn/web-common";
import { COMPARE_COST_INVALIDATE } from "@penn/web-common";
import {
  configuredInvestmentCacheSignature,
  resolveConfiguredMachinePriceOverride,
  resolveRecommendedConfigurationTotal,
} from "@/utils/configured-investment";
import {
  mspModelConstantOverrides,
  mspOverrideSignature,
} from "@/utils/mspe-override-signature";

import { getApplication, type ApplicationDetail } from "@/api/applications";
import {
  applicationToCalculateInput,
  invalidateCalculatePreviewRequestCache,
  isApplicationValidForCalculate,
  postCalculatePreview,
  type CalculatePreviewResponse,
  type MachineCostMode,
} from "@/api/calculate";
import type { RegionCode, SelectorProduct } from "@/api/filter";
import {
  deriveAppsForCompare,
  isTSeriesMachineId,
} from "@/utils/tseries-split";
import { invalidateConfiguredInvestmentRequestCache } from "@/utils/compute-configured-investment";
import {
  isRequestAbortError,
  toCalculationRateLimitError,
} from "@/utils/calculation-request-control";

const CALCULATION_DEBOUNCE_MS = 400;

export type ProductCostState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "ok";
      data: Extract<CalculatePreviewResponse, { available: true }>["result"];
      assignmentKey?: string;
      assignmentRequiredMachines?: number;
      recommendedConfigurationTotal?: string;
    }
  | { status: "unavailable"; reason: string; assignmentKey?: string }
  | { status: "error"; message: string };

function appsCacheKey(details: ApplicationDetail[]): string {
  return details
    .map((d) =>
      [
        d.applicationId,
        d.annualVolume,
        d.batchSize,
        d.fasteners
          .map((f) =>
            [f.brand, f.type, f.qtyPerPart, f.qtyPerPartA, f.qtyPerPartB].join(
              ":",
            ),
          )
          .join("|"),
      ].join(";"),
    )
    .sort()
    .join("||");
}

function applicationIdsForProduct(
  productId: string,
  selectedMachineByApp: Record<string, string>,
  appDetails: Array<{ applicationId: string }>,
  compareApps: Array<{ applicationId: string }>,
  splitEnabled: boolean,
): string[] {
  const scoped = Object.entries(selectedMachineByApp)
    .filter(([, machineId]) => machineId === productId)
    .map(([appId]) => appId);
  if (scoped.length > 0) return scoped;
  if (splitEnabled)
    return compareApps.map((a) => a.applicationId).filter(Boolean);
  return appDetails.map((a) => a.applicationId).filter(Boolean);
}

async function decorateCostWithAssignment(
  entry: Extract<ProductCostState, { status: "ok" }>,
  args: {
    productId: string;
    region: RegionCode;
    appIds: string[];
    configureAppIds: string[];
    mspAutoFeedEnabled: boolean;
    signal?: AbortSignal;
  },
): Promise<Extract<ProductCostState, { status: "ok" }>> {
  const assignmentKey = [...args.appIds].sort().join(",");
  const assignmentRequiredMachines = requiredMachinesForAssignment(
    entry.data,
    args.appIds,
  );
  const recommendedConfigurationTotal =
    assignmentRequiredMachines === undefined
      ? undefined
      : await resolveRecommendedConfigurationTotal({
          productId: args.productId,
          region: args.region,
          applicationIds: args.configureAppIds,
          mspAutoFeedEnabled: args.mspAutoFeedEnabled,
          requiredMachines: assignmentRequiredMachines,
          signal: args.signal,
        });
  return {
    ...entry,
    assignmentKey,
    assignmentRequiredMachines,
    recommendedConfigurationTotal,
  };
}

function requiredMachinesForAssignment(
  result: Extract<CalculatePreviewResponse, { available: true }>["result"],
  applicationIds: string[],
): number | undefined {
  const ids = new Set(applicationIds);
  const rows = result.perApp.filter(
    (row) => row.applicationId && ids.has(row.applicationId),
  );
  if (rows.length !== ids.size) return undefined;

  let utilization = 0;
  for (const row of rows) {
    const value = Number(row.machineUtilization);
    if (!Number.isFinite(value)) return undefined;
    utilization += value;
  }
  return Math.max(1, Math.ceil(utilization));
}

type UseComparisonCostsArgs = {
  region: RegionCode;
  products: SelectorProduct[];
  selectedProductIds: string[];
  applicationSummaries: Array<{ applicationId: string }>;
  /** appId → productId assigned on comparison cards (POC machine per app). */
  selectedMachineByApp: Record<string, string>;
  machineCostMode: MachineCostMode;
  /** productId → true = Auto-Feed On (G14 default). */
  mspAutoFeedByProductId: Record<string, boolean>;
  /** Q-OPT-02 — comparison workspace UUID; warms optimize cache on each calc. */
  optimizeSessionId?: string;
  enabled: boolean;
};

type InflightRequest = {
  generation: number;
  token: symbol;
};

export function useComparisonCosts({
  region,
  products,
  selectedProductIds,
  applicationSummaries,
  selectedMachineByApp,
  machineCostMode,
  mspAutoFeedByProductId,
  optimizeSessionId,
  enabled,
}: UseComparisonCostsArgs) {
  const [appDetails, setAppDetails] = useState<ApplicationDetail[] | null>(
    null,
  );
  const [appDetailsError, setAppDetailsError] = useState<string | null>(null);
  const [costByProductId, setCostByProductId] = useState<
    Record<string, ProductCostState>
  >({});
  // Bumped on every COMPARE_COST_INVALIDATE so the recompute effect below
  // re-runs. Without this, an invalidate clears `costByProductId` but no
  // dependency of the recompute effect changes — so cards that were cleared
  // (e.g. by the configure modal persisting a new selection) never recompute
  // and the machine prices stay blank once the modal closes.
  const [invalidateNonce, setInvalidateNonce] = useState(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const [calculationRetryMessage, setCalculationRetryMessage] = useState<
    string | null
  >(null);
  const cacheRef = useRef(
    new Map<
      string,
      Extract<ProductCostState, { status: "ok" } | { status: "unavailable" }>
    >(),
  );
  const inflightRef = useRef(new Map<string, InflightRequest>());
  const requestGenerationRef = useRef(0);
  const calculationControllerRef = useRef<AbortController | null>(null);
  const retryAtRef = useRef(0);
  /** POC: Heger machine qty follows `summary.requiredMachines` from prior calc. */
  const requiredMachinesByProductRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const onInvalidate = () => {
      requestGenerationRef.current += 1;
      calculationControllerRef.current?.abort();
      invalidateConfiguredInvestmentRequestCache();
      invalidateCalculatePreviewRequestCache();
      cacheRef.current.clear();
      inflightRef.current.clear();
      setCostByProductId({});
      setInvalidateNonce((n) => n + 1);
    };
    window.addEventListener(COMPARE_COST_INVALIDATE, onInvalidate);
    return () =>
      window.removeEventListener(COMPARE_COST_INVALIDATE, onInvalidate);
  }, []);

  const selectedProducts = useMemo(
    () => products.filter((p) => selectedProductIds.includes(p.productId)),
    [products, selectedProductIds],
  );

  // POC parity (issue 2): when a T-Series press is in the comparison, every base
  // app is split into its A / B sides and ALL selected machines are recomputed
  // against the split list (`deriveAppsForCompare`). When no T-Series machine is
  // selected, `compareApps` === the base apps, so behaviour is unchanged.
  const { apps: compareApps, splitEnabled } = useMemo(
    () => deriveAppsForCompare(appDetails ?? [], selectedProducts),
    [appDetails, selectedProducts],
  );

  const appsKey = appDetails ? appsCacheKey(appDetails) : "";
  // The cache/calc key keys on the *derived* apps so toggling a T-Series machine
  // (which changes the split) busts cached non-split results for every card.
  const calcAppsKey = compareApps.length > 0 ? appsCacheKey(compareApps) : "";
  const appsValid =
    appDetails != null &&
    appDetails.length > 0 &&
    appDetails.every(isApplicationValidForCalculate);

  useEffect(() => {
    if (!enabled || applicationSummaries.length === 0) {
      setAppDetails(null);
      setAppDetailsError(null);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const details = await Promise.all(
          applicationSummaries.map((a) =>
            getApplication(a.applicationId, controller.signal),
          ),
        );
        if (!controller.signal.aborted) {
          setAppDetails(details);
          setAppDetailsError(null);
        }
      } catch (e) {
        if (isRequestAbortError(e)) return;
        if (controller.signal.aborted) return;
        const rateLimitError = toCalculationRateLimitError(e);
        controller.abort(rateLimitError ?? e);
        setAppDetails(null);
        setAppDetailsError(
          rateLimitError?.message ??
            getApiErrorMessage(e, "Failed to load applications"),
        );
      }
    })();
    return () => {
      controller.abort();
    };
  }, [enabled, applicationSummaries]);

  const runCalculate = useCallback(
    async (
      productId: string,
      generation: number,
      signal: AbortSignal,
    ) => {
      if (!enabled || !appsValid || !appDetails) return;
      const isCurrentRequest = () =>
        requestGenerationRef.current === generation && !signal.aborted;
      if (!isCurrentRequest()) return;

      const autoFeedOn = mspAutoFeedByProductId[productId] ?? true;
      const overrideSig = mspOverrideSignature(productId, autoFeedOn);
      const requiredMachines =
        requiredMachinesByProductRef.current[productId] ?? 1;
      const appIds = applicationIdsForProduct(
        productId,
        selectedMachineByApp,
        appDetails,
        compareApps,
        splitEnabled,
      );
      // POC parity: T-Series auto-selected configure totals use scoped A/B app ids
      // (jig × appCount), not the unsplit base application id.
      const configureAppIds =
        splitEnabled && isTSeriesMachineId(productId)
          ? compareApps.map((a) => a.applicationId)
          : appIds;
      const configureSig = configuredInvestmentCacheSignature(
        productId,
        requiredMachines,
        configureAppIds,
      );
      const cacheKey = `${region}|${productId}|${calcAppsKey}|${machineCostMode}|${overrideSig}|${configureSig}|${optimizeSessionId ?? ""}`;
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        if (cached.status === "ok") {
          const decorated = await decorateCostWithAssignment(cached, {
            productId,
            region,
            appIds,
            configureAppIds,
            mspAutoFeedEnabled: autoFeedOn,
            signal,
          });
          if (!isCurrentRequest()) return;
          setCostByProductId((prev) =>
            isCurrentRequest() ? { ...prev, [productId]: decorated } : prev,
          );
        } else {
          const assignmentKey = [...appIds].sort().join(",");
          setCostByProductId((prev) =>
            isCurrentRequest()
              ? {
                  ...prev,
                  [productId]: { ...cached, assignmentKey },
                }
              : prev,
          );
        }
        return;
      }
      if (inflightRef.current.get(cacheKey)?.generation === generation) return;
      const requestToken = Symbol(cacheKey);
      inflightRef.current.set(cacheKey, { generation, token: requestToken });
      setCostByProductId((prev) =>
        isCurrentRequest()
          ? {
              ...prev,
              [productId]: { status: "loading" },
            }
          : prev,
      );

      try {
        let machinePriceOverride = await resolveConfiguredMachinePriceOverride({
          productId,
          region,
          applicationIds: configureAppIds,
          mspAutoFeedEnabled: autoFeedOn,
          requiredMachines,
          signal,
        });
        if (!isCurrentRequest()) return;

        let res = await postCalculatePreview(
          {
            region,
            productId,
            machineCostMode,
            applications: compareApps.map(applicationToCalculateInput),
            modelConstantOverrides: mspModelConstantOverrides(
              productId,
              autoFeedOn,
            ),
            ...(machinePriceOverride ? { machinePriceOverride } : {}),
            ...(optimizeSessionId ? { optimizeSessionId } : {}),
          },
          signal,
        );
        if (!isCurrentRequest()) return;

        if (res.available) {
          const rm = Math.max(
            1,
            Math.ceil(Number(res.result.summary.requiredMachines) || 1),
          );
          const prevRm = requiredMachinesByProductRef.current[productId] ?? 1;
          requiredMachinesByProductRef.current[productId] = rm;

          if (rm !== prevRm) {
            const override2 = await resolveConfiguredMachinePriceOverride({
              productId,
              region,
              applicationIds: configureAppIds,
              mspAutoFeedEnabled: autoFeedOn,
              requiredMachines: rm,
              signal,
            });
            if (!isCurrentRequest()) return;
            if (override2 && override2 !== machinePriceOverride) {
              machinePriceOverride = override2;
              res = await postCalculatePreview(
                {
                  region,
                  productId,
                  machineCostMode,
                  applications: compareApps.map(applicationToCalculateInput),
                  modelConstantOverrides: mspModelConstantOverrides(
                    productId,
                    autoFeedOn,
                  ),
                  machinePriceOverride,
                  ...(optimizeSessionId ? { optimizeSessionId } : {}),
                },
                signal,
              );
              if (!isCurrentRequest()) return;
            }
          }
        }

        const finalRm =
          requiredMachinesByProductRef.current[productId] ?? requiredMachines;
        const finalCacheKey = `${region}|${productId}|${calcAppsKey}|${machineCostMode}|${overrideSig}|${configuredInvestmentCacheSignature(productId, finalRm, configureAppIds)}|${optimizeSessionId ?? ""}`;

        let entry: ProductCostState;
        if (res.available) {
          const baseOk: Extract<ProductCostState, { status: "ok" }> = {
            status: "ok",
            data: res.result,
          };
          entry = await decorateCostWithAssignment(baseOk, {
            productId,
            region,
            appIds,
            configureAppIds,
            mspAutoFeedEnabled: autoFeedOn,
            signal,
          });
          if (!isCurrentRequest()) return;
          cacheRef.current.set(finalCacheKey, entry);
        } else {
          entry = {
            status: "unavailable",
            reason: res.reason,
            assignmentKey: [...appIds].sort().join(","),
          };
          cacheRef.current.set(finalCacheKey, entry);
        }
        setCostByProductId((prev) =>
          isCurrentRequest() ? { ...prev, [productId]: entry } : prev,
        );
      } catch (e) {
        const rateLimitError = toCalculationRateLimitError(e);
        if (rateLimitError) throw rateLimitError;
        if (isRequestAbortError(e)) return;
        // Prefer the backend's validation message (§J error envelope:
        // `{ error: { code, message } }`) — e.g. the 422 invalid-annual-volume
        // guard — over the raw Axios "status code 4xx" string.
        const message = getApiErrorMessage(e, "Calculate failed");
        setCostByProductId((prev) =>
          isCurrentRequest()
            ? {
                ...prev,
                [productId]: { status: "error", message },
              }
            : prev,
        );
      } finally {
        if (inflightRef.current.get(cacheKey)?.token === requestToken) {
          inflightRef.current.delete(cacheKey);
        }
      }
    },
    [
      enabled,
      appsValid,
      appDetails,
      compareApps,
      calcAppsKey,
      splitEnabled,
      region,
      machineCostMode,
      mspAutoFeedByProductId,
      selectedMachineByApp,
      optimizeSessionId,
    ],
  );

  useEffect(() => {
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    const controller = new AbortController();
    calculationControllerRef.current?.abort();
    calculationControllerRef.current = controller;
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (!enabled) {
      setCostByProductId({});
      setCalculationRetryMessage(null);
    } else if (!appsValid) {
      setCostByProductId({});
      setCalculationRetryMessage(null);
    } else {
      const waitForRateLimit = Math.max(0, retryAtRef.current - Date.now());
      timer = setTimeout(
        () => {
          void (async () => {
            setCalculationRetryMessage(null);
            for (const p of selectedProducts) {
              if (controller.signal.aborted) return;
              try {
                await runCalculate(
                  p.productId,
                  generation,
                  controller.signal,
                );
              } catch (error) {
                const rateLimitError = toCalculationRateLimitError(error);
                if (!rateLimitError || controller.signal.aborted) return;

                retryAtRef.current = rateLimitError.retryAtMs;
                setCalculationRetryMessage(rateLimitError.message);
                controller.abort(rateLimitError);
                const retryInMs = Math.max(
                  0,
                  rateLimitError.retryAtMs - Date.now(),
                );
                timer = setTimeout(
                  () => setRetryNonce((nonce) => nonce + 1),
                  retryInMs,
                );
                return;
              }
            }
          })();
        },
        Math.max(CALCULATION_DEBOUNCE_MS, waitForRateLimit),
      );
    }

    return () => {
      if (timer) clearTimeout(timer);
      controller.abort();
      if (calculationControllerRef.current === controller) {
        calculationControllerRef.current = null;
      }
      if (requestGenerationRef.current === generation) {
        requestGenerationRef.current += 1;
      }
    };
  }, [
    enabled,
    appsValid,
    selectedProducts,
    runCalculate,
    invalidateNonce,
    retryNonce,
  ]);

  // Keep the effect stable when callers recreate an equivalent IDs array while
  // still reading the current array when its value signature changes.
  const selectedProductIdsRef = useRef(selectedProductIds);
  selectedProductIdsRef.current = selectedProductIds;
  const clearStale = useMemo(
    () => selectedProductIds.join(","),
    [selectedProductIds],
  );

  useEffect(() => {
    setCostByProductId((prev) => {
      const next: Record<string, ProductCostState> = {};
      for (const id of selectedProductIdsRef.current) {
        if (prev[id]) next[id] = prev[id];
      }
      return next;
    });
  }, [clearStale]);

  return {
    costByProductId,
    appDetails,
    /** Apps actually sent to the cost engine — split into A/B sides when a
     * T-Series press is selected (issue 2), else the base apps. */
    compareApps,
    splitEnabled,
    appDetailsError,
    calculationRetryMessage,
    appsValid,
    appsKey,
  };
}
